import { BRAND_CONFIG } from '@@brand'
import { GQL_ID } from '@@types'
import { useTranslation } from 'react-i18next'

import { useFetchUserOrder } from '@entities/Order/hook/userOrder.gql'

import { useBrandSquareLogo } from '@shared/hooks'
import { formatPrice } from '@shared/uttils'
import { ORDER_STATUS_IN_PROGRESS } from '@shared/uttils/order'

import styles from './styles.module.scss'

interface Props {
  orderId: GQL_ID
}

export default function OrderSummary ({ orderId }: Props) {
  const {
    order,
    loading,
    error
  } = useFetchUserOrder(orderId)

  const { t: to } = useTranslation('order')
  const brandBonusIcon = useBrandSquareLogo(order?.shop?.brand)


  const totalCashback = order?.totalCashback ?? 0
  const hasAssembledDifference = order?.assembled_total && order?.assembled_total > 0 && order.assembled_total !== order.total

  if ((loading || error) && !order) {
    return (
      <div className={styles.summary}>
        <h5>{to('order.total')}:</h5>
        <p>{loading ? to('common.loading') : to('common.error')}</p>
      </div>
    )
  }

  if (!order) {
    return null
  }

  return (
    <div className={styles.summary}>
      <h5>{to('order.total')}:</h5>

      {/* Стоимость товаров */}
      {hasAssembledDifference ? (
        <div>
          <SummaryRow
            label={to('order.product-total')}
            value={formatPrice(order.assembled_total)}
          />
          <SummaryRow 
            isSubRow
            label={to('order.ordered')}
            value={formatPrice(order.total)}
          />
        </div>
      ) : (
        <SummaryRow
          label={to('order.product-total')}
          value={formatPrice(order.assembled_total > 0 ? order.assembled_total : order.total)}
        />
      )}

      {/* Стоимость доставки */}
      {order?.deliveryCost ? (
        <SummaryRow
          label={to('order.delivery-price')}
          value={formatPrice(order.deliveryCost)}
        />
      ) : null}

      {/* Скидка */}
      {order?.totalDiscount ? (
        <SummaryRow
          label={to('order.discount')}
          value={`-${formatPrice(order.totalDiscount)}`}
        />
      ) : null}

      {/* Итого */}
      <SummaryRow
        className={styles.summary__bonus}
        label={to('order.total')}
        value={formatPrice(order?.customerFinalCost || 0)}
        isTotal
      />

      {/* Кэшбэк */}
      {totalCashback > 0 && (
        <SummaryRow
          isBonus
          label={
            ORDER_STATUS_IN_PROGRESS.includes(order.status)
              ? to('add-bonus_history')
              : to('add-bonus')
          }
          bonusIconSrc={brandBonusIcon || undefined}
          value={`${totalCashback}`}
        />
      )}
    </div>
  )
}

interface SummaryRowProps {
  label: string;
  value: string;
  isTotal?: boolean;
  isBonus?: boolean;
  isSubRow?: boolean;
  className?: string;
  bonusIconSrc?: string;
}

function SummaryRow ({
  label,
  value,
  isTotal = false,
  isBonus = false,
  isSubRow = false,
  className = '',
  bonusIconSrc,
}: SummaryRowProps) {
  return (
    <p className={className} data-subrow={isSubRow || undefined}>
      {label}:
      <span data-total={isTotal || undefined}>
        {isBonus && (
          <img
            src={bonusIconSrc || BRAND_CONFIG.images.loyaltyIcon}
            alt=""
          />
        )}
        {value}
      </span>
    </p>
  )
}